import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import {
  Button,
  Card,
  ErrorState,
  Field,
  Money,
  PageHeader,
  Select,
  TextField,
} from "../components/ui";
import { dollarsToCents, today } from "../lib/format";
import type { Account, Vendor } from "../lib/models";
import { useInvoicePilot } from "./InvoicePilotLink";
import { canUsePaymentAccount, paymentAccountLabel, merchantClearingHint } from "../lib/paymentAccounts";
import { canUsePurchaseAccount } from "../lib/purchaseAccounts";

interface LineDraft {
  accountId: string;
  description: string;
  amount: string;
}

const blankLine: LineDraft = { accountId: "", description: "", amount: "0.00" };

export default function ExpenseForm() {
  const { currentOrg } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const pilot = useInvoicePilot();

  const { data: vendors } = useQuery({
    queryKey: ["vendors", currentOrg?.id],
    queryFn: () => api.get<Vendor[]>("/vendors"),
    enabled: !!currentOrg,
  });
  const { data: accounts } = useQuery({
    queryKey: ["accounts", currentOrg?.id],
    queryFn: () => api.get<Account[]>("/accounts"),
    enabled: !!currentOrg,
  });
  const payAccounts = (accounts ?? []).filter(
    (a) => pilot.data?.enabled ? canUsePaymentAccount(a, 'made', currency) : a.type === "asset" || a.type === "liability",
  );
  const categoryAccounts = (accounts ?? []).filter(
    (a) => pilot.data?.enabled ? canUsePurchaseAccount(a, currency) : a.type === "expense" || a.type === "asset",
  );

  const [header, setHeader] = useState({
    vendorId: "",
    payeeName: "",
    paymentAccountId: "",
    txnDate: today(),
    paymentType: "",
    memo: "",
  });
  const [lines, setLines] = useState<LineDraft[]>([{ ...blankLine }]);

  const total = lines.reduce((s, l) => s + dollarsToCents(l.amount), 0);

  function updateLine(i: number, patch: Partial<LineDraft>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>("/expenses", {
        vendorId: header.vendorId || undefined,
        payeeName: header.payeeName || undefined,
        paymentAccountId: header.paymentAccountId,
        txnDate: header.txnDate,
        paymentType: header.paymentType || undefined,
        memo: header.memo || undefined,
        lines: lines.map((l) => ({
          accountId: l.accountId,
          description: l.description || undefined,
          amount: dollarsToCents(l.amount),
          billable: false,
        })),
      }),
    onSuccess: (expense) => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      navigate(pilot.data?.enabled ? `/expenses/${encodeURIComponent(expense.id)}?saved=1` : "/expenses");
    },
  });

  const valid =
    !pilot.isLoading && header.paymentAccountId && (!pilot.data?.enabled || payAccounts.some(a=>a.id===header.paymentAccountId)) && lines.every((l) => l.accountId && (!pilot.data?.enabled || categoryAccounts.some(a => a.id === l.accountId))) && total > 0;

  return (
    <div>
      <PageHeader title="New expense" subtitle="Record a direct purchase" />
      <Card className="p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <Field label="Payee">
            <Select
              value={header.vendorId}
              onChange={(e) => {
                const v = (vendors ?? []).find((x) => x.id === e.target.value);
                setHeader({
                  ...header,
                  vendorId: e.target.value,
                  payeeName: v?.displayName ?? header.payeeName,
                });
              }}
            >
              <option value="">— select vendor —</option>
              {(vendors ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.displayName}
                </option>
              ))}
            </Select>
          </Field>
          <Field required label="Payment account" hint={pilot.data?.enabled ? (merchantClearingHint(payAccounts.find(a => a.id === header.paymentAccountId), 'made') ?? 'Choose the bank, cash, clearing or credit card account used for this purchase.') : undefined}>
            <Select
              value={header.paymentAccountId}
              onChange={(e) =>
                setHeader({ ...header, paymentAccountId: e.target.value })
              }
            >
              <option value="">— select —</option>
              {payAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {pilot.data?.enabled ? paymentAccountLabel(a) : a.code + ' ' + a.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field required label="Date">
            <TextField
              type="date"
              value={header.txnDate}
              onChange={(e) => setHeader({ ...header, txnDate: e.target.value })}
            />
          </Field>
          <Field label="Payment type">
            <TextField
              value={header.paymentType}
              placeholder="card, check…"
              onChange={(e) =>
                setHeader({ ...header, paymentType: e.target.value })
              }
            />
          </Field>
        </div>

        <div className="mt-6 overflow-x-auto">
          {pilot.data?.enabled && <p id="purchase-category-guidance" className="mb-3 text-sm text-gray-500">Choose what you bought: an expense category or an asset such as equipment, inventory or a prepaid cost. Payment account above identifies how you paid.</p>}
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="th">Category (required)</th>
                <th className="th">Description</th>
                <th className="th w-32 text-right">Amount (required)</th>
                <th className="th w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2 pr-2">
                    <Select
                      aria-label={`Category, line ${i + 1}`} aria-required="true" value={l.accountId}
                      aria-describedby={pilot.data?.enabled ? 'purchase-category-guidance' : undefined}
                      onChange={(e) =>
                        updateLine(i, { accountId: e.target.value })
                      }
                    >
                      <option value="">— select —</option>
                      {categoryAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} {a.name}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="py-2 pr-2">
                    <TextField
                      value={l.description}
                      onChange={(e) =>
                        updateLine(i, { description: e.target.value })
                      }
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <TextField
                      type="number"
                      step="0.01"
                      className="text-right"
                      aria-label={`Amount, line ${i + 1}`} aria-required="true" value={l.amount}
                      onChange={(e) => updateLine(i, { amount: e.target.value })}
                    />
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      className="text-gray-300 hover:text-red-500"
                      onClick={() =>
                        setLines((ls) => ls.filter((_, idx) => idx !== i))
                      }
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setLines((ls) => [...ls, { ...blankLine }])}
          >
            + Add line
          </Button>
          <div className="text-right">
            <span className="mr-3 text-sm text-gray-500">Total</span>
            <span className="text-xl font-semibold">
              <Money cents={total} currency={currency} />
            </span>
          </div>
        </div>

        <div className="mt-4">
          <Field label="Memo">
            <TextField
              value={header.memo}
              onChange={(e) => setHeader({ ...header, memo: e.target.value })}
            />
          </Field>
        </div>

        {create.error && (
          <div className="mt-4">
            <ErrorState error={create.error} />
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => navigate("/expenses")}>
            Cancel
          </Button>
          <Button disabled={!valid || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? "Saving…" : "Save expense"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
